import React from 'react';
import styled from 'styled-components';

import {
  Accordion,
  AccordionProps,
  ArrowDownIcon,
  FlexColumn,
  SvgIcon,
} from '@make-software/cspr-design';
import { BodyText, Table } from '@block-explorer/ui';
import DetailsTableRow from '../details-table-row/details-table-row';
import DetailsValueTableData from '../details-value-table-data/details-value-table-data';

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: 4,
}));

const CollapsibleRow = styled.div(({ theme }) =>
  theme.withMedia({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 20,
    paddingRight: 20,
    height: 40,
    background: theme.styleguideColors.fillSecondary,
    '&:hover': {
      span: {
        color: theme.styleguideColors.contentRed,
      },
    },
  })
);

const FeedCollapsibleRow = styled.div(({ theme }) =>
  theme.withMedia({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 40,
    '&:hover': {
      span: {
        color: theme.styleguideColors.contentBlue,
      },
    },
  })
);

const StyledIcon = styled(SvgIcon)(({ theme }) =>
  theme.withMedia({
    color: theme.styleguideColors.contentTertiary,
    [`${CollapsibleRow}:hover &`]: {
      color: theme.styleguideColors.contentRed,
    },
  })
);

const StyledAccountInfoTable = styled(Table)(({ theme }) =>
  theme.withMedia({
    marginBottom: 16,
  })
);

export const AccountInfoTable = ({ renderData: renderTableData }) => {
  return (
    <StyledAccountInfoTable
      renderData={(renderProps) => (
        <DetailsTableRow>
          <DetailsValueTableData>
            {renderTableData(renderProps)}
          </DetailsValueTableData>
        </DetailsTableRow>
      )}
    />
  );
};

export const AccountInfoAccordion = ({
  children,
  withFeed = false,
  ...props
}: React.PropsWithChildren<AccordionProps & { withFeed?: boolean }>) => {
  const CollapsibleRowComponent = withFeed
    ? FeedCollapsibleRow
    : CollapsibleRow;

  return (
    <StyledAccordion {...props} style={{}}>
      {({ isOpen }) => (
        <CollapsibleRowComponent>
          <BodyText size={1}>{children}</BodyText>
          <StyledIcon src={ArrowDownIcon} rotate={isOpen} />
        </CollapsibleRowComponent>
      )}
    </StyledAccordion>
  );
};

export const AccordionItem = ({
  children,
  title,
  gap = 4,
  ...props
}: React.PropsWithChildren<any>) => {
  return (
    <DetailsTableRow>
      <DetailsValueTableData {...props}>
        <FlexColumn gap={gap}>
          <BodyText size={3} variation={'darkGray'}>
            {title}
          </BodyText>
          {children}
        </FlexColumn>
      </DetailsValueTableData>
    </DetailsTableRow>
  );
};
